import type { ComponentType, ReactNode } from "react"
import { cn } from "@/lib/utils"

// The console's page frame, as PROPS — no router, no shell context, no permission read.
//
// It exists because the design graph could not show anyone what a screen looks like. A cell
// rendered `CashHomeDocument` on a bare white rectangle, so every review argued about a
// document floating in a void while the thing that ships is a document inside a page card
// beside a sidebar. `ConsoleChrome` is that frame, and it takes fixture nav, so a sheet cell
// and the real app can render the same chrome from different data.
//
// The register (see design/cash-ops-ui-spec.md §0):
//   canvas   the grey desk (--canvas), and the sidebar is TRANSPARENT on it — a rail with its
//            own fill would be a second surface competing with the page card
//   page     one white card, 24px radius, 1px hairline, 32px padding, and it scrolls
//   sidebar  40px rows, flat grey fill + ink text when active, sub-items indented behind a
//            left hairline. No pill, no bevel, no coloured edge.

// The rail is unconditionally visible: this chrome frames desktop page shots and the routed
// shell brings its own collapsible sidebar, so a `hidden md:flex` here bought nothing and lost
// to a duplicated `.hidden` in the design-graph viewer's concatenated stylesheet.

export type ConsoleChromeSubItem = {
  key: string
  label: ReactNode
  href?: string
  active?: boolean
}

export type ConsoleChromeNavItem = {
  key: string
  label: ReactNode
  icon?: ComponentType<{ className?: string }>
  href?: string
  active?: boolean
  /** Rendered only when the parent is active — a nav that expands everything is a menu. */
  children?: ConsoleChromeSubItem[]
}

/**
 * The page card's surface, exported so the routed shell dresses its `<SidebarInset>` with the
 * SAME classes this component draws. Two frames that must not drift are one constant.
 */
export const CONSOLE_PAGE_CARD = "rounded-2xl border border-border bg-background"

/** The inset the page card sits in: an 8px gutter of canvas on every side but the sidebar's. */
export const CONSOLE_PAGE_INSET = "p-2 ps-0"

const ROW =
  "flex h-10 w-full items-center gap-2.5 rounded-lg px-3 text-sm text-muted-foreground transition-colors duration-150 outline-none hover:bg-accent/70 hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/30"

const ROW_ACTIVE = "bg-accent font-medium text-foreground"

function NavRow({ item }: { item: ConsoleChromeNavItem }) {
  const Icon = item.icon
  return (
    <li>
      <a
        href={item.href ?? "#"}
        aria-current={item.active ? "page" : undefined}
        className={cn(ROW, item.active && ROW_ACTIVE)}
      >
        {Icon ? <Icon className="size-4 shrink-0" /> : null}
        <span className="min-w-0 truncate">{item.label}</span>
      </a>
      {item.active && item.children?.length ? (
        // The hairline is the indent: a sub-list that only indents reads as a wrapped label,
        // and a sub-list that draws its own box reads as a second nav.
        <ul className="ms-5 mt-0.5 space-y-0.5 border-s border-border ps-3">
          {item.children.map((child) => (
            <li key={child.key}>
              <a
                href={child.href ?? "#"}
                aria-current={child.active ? "page" : undefined}
                className={cn(ROW, "h-9 gap-0 text-[13px]", child.active && ROW_ACTIVE)}
              >
                <span className="min-w-0 truncate">{child.label}</span>
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  )
}

export type ConsoleChromeProps = {
  /** The primary nav. Omitted entirely when the caller supplies its own `sidebar`. */
  nav?: ConsoleChromeNavItem[]
  /** Bottom utility rows — Support, Documentation. */
  utility?: ConsoleChromeNavItem[]
  /** The org switcher's label ("Norn"). */
  org?: ReactNode
  /** The card above the utility rows — an upsell, a rollout note, whatever the shell has. */
  promo?: ReactNode
  /** Replaces the whole rail. The routed shell passes its real, permission-filtered sidebar. */
  sidebar?: ReactNode
  /** Rendered above the page card's content, flush with it (breadcrumbs, a site header). */
  header?: ReactNode
  /** False when the caller's content owns its own padding (a master–detail screen). */
  padded?: boolean
  className?: string
  children: ReactNode
}

export function ConsoleChrome({
  nav,
  utility,
  org,
  promo,
  sidebar,
  header,
  padded = true,
  className,
  children,
}: ConsoleChromeProps) {
  return (
    <div data-slot="console-chrome" className={cn("flex min-h-full w-full bg-canvas text-foreground", className)}>
      {sidebar ?? (
        <nav
          data-slot="console-chrome-sidebar"
          aria-label="Console"
          className="flex w-[248px] shrink-0 flex-col px-3 py-4"
        >
          <a href="/" className="mb-4 flex h-10 items-center gap-2.5 rounded-lg px-3">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-lg bg-primary text-[12px] font-semibold text-primary-foreground">
              N
            </span>
          </a>

          <ul className="space-y-0.5">
            {(nav ?? []).map((item) => (
              <NavRow key={item.key} item={item} />
            ))}
          </ul>

          <div className="mt-auto space-y-3 pt-6">
            {promo ? <div className={cn(CONSOLE_PAGE_CARD, "rounded-lg p-4")}>{promo}</div> : null}
            <ul className="space-y-0.5">
              {(utility ?? []).map((item) => (
                <NavRow key={item.key} item={item} />
              ))}
            </ul>
            {org ? (
              <div className="flex h-12 items-center gap-2.5 rounded-lg border-t border-border px-3 pt-3 text-sm font-medium">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-lg bg-primary text-[12px] font-semibold text-primary-foreground">
                  N
                </span>
                <span className="min-w-0 truncate">{org}</span>
                <svg aria-hidden viewBox="0 0 16 16" className="ms-auto size-4 text-muted-foreground">
                  <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
            ) : null}
          </div>
        </nav>
      )}

      <div className={cn("flex min-w-0 flex-1 flex-col", CONSOLE_PAGE_INSET)}>
        <div className={cn(CONSOLE_PAGE_CARD, "flex min-h-full min-w-0 flex-1 flex-col overflow-hidden")}>
          {header}
          <div className={cn("min-w-0 flex-1", padded && "p-8")}>{children}</div>
        </div>
      </div>
    </div>
  )
}
